import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { money } from "@/lib/format";
import { BuscadorArticulo } from "@/components/BuscadorArticulo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const lineaVacia = { articulo_id: null, descripcion: "", categoria: "", cantidad: 1,
                            precio_unitario: "", existencia: null };

const faltaExistencia = (l) => l.articulo_id && l.existencia !== null &&
  Number(l.cantidad || 0) > Number(l.existencia);

/** Editor de las líneas de una venta: artículos del inventario o conceptos escritos a mano. */
export const LineasVenta = ({ lineas, onChange, articulos }) => {
  const set = (i, cambios) => onChange(lineas.map((l, j) => (j === i ? { ...l, ...cambios } : l)));

  const elegir = (i, a) => set(i, {
    articulo_id: a.id, descripcion: a.nombre + (a.talla ? ` talla ${a.talla}` : ""),
    categoria: a.categoria, precio_unitario: String(a.precio), existencia: a.existencia,
  });

  const total = lineas.reduce((s, l) => s + Number(l.cantidad || 0) * Number(l.precio_unitario || 0), 0);

  return (
    <div className="space-y-4 sm:col-span-2" data-testid="lineas-venta">
      {lineas.map((l, i) => (
        <div key={i} className="rounded-md border border-border bg-muted/20 p-4" data-testid={`linea-venta-${i}`}>
          <div className="grid gap-3 sm:grid-cols-12">
            {articulos.length > 0 && (
              <div className="space-y-1 sm:col-span-12">
                <Label className="text-xs">Artículo del inventario</Label>
                <BuscadorArticulo articulos={articulos} valor={l.articulo_id}
                                  testId={`articulo-linea-${i}`} onChange={(a) => elegir(i, a)} />
              </div>
            )}
            <div className="space-y-1 sm:col-span-6">
              <Label className="text-xs">Descripción</Label>
              <Input value={l.descripcion} data-testid={`linea-descripcion-${i}`}
                     placeholder="Uniforme, libro o material"
                     onChange={(e) => set(i, { descripcion: e.target.value })} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Cantidad</Label>
              <Input type="number" min="1" value={l.cantidad} data-testid={`linea-cantidad-${i}`}
                     onChange={(e) => set(i, { cantidad: e.target.value })} />
            </div>
            <div className="space-y-1 sm:col-span-3">
              <Label className="text-xs">Precio (RD$)</Label>
              <Input type="number" value={l.precio_unitario} data-testid={`linea-precio-${i}`}
                     onChange={(e) => set(i, { precio_unitario: e.target.value })} />
            </div>
            <div className="flex items-end justify-between gap-2 sm:col-span-1">
              <Button size="sm" variant="ghost" data-testid={`quitar-linea-${i}`}
                      disabled={lineas.length === 1}
                      onClick={() => onChange(lineas.filter((_, j) => j !== i))}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="text-muted-foreground">
              {l.existencia !== null && l.articulo_id ? `Existencia disponible: ${l.existencia}` : "Concepto libre"}
            </span>
            <span className="font-mono font-semibold">
              {money(Number(l.cantidad || 0) * Number(l.precio_unitario || 0))}
            </span>
          </div>
          {faltaExistencia(l) && (
            <p className="mt-2 flex items-center gap-2 text-xs text-destructive"
               data-testid={`aviso-existencia-${i}`}>
              <AlertTriangle className="h-3.5 w-3.5" />
              Pide {l.cantidad} y solo hay {l.existencia}. Marque “vender igual” para continuar.
            </p>
          )}
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" size="sm" className="rounded-full" data-testid="agregar-linea-venta"
                onClick={() => onChange([...lineas, { ...lineaVacia }])}>
          <Plus className="mr-2 h-4 w-4" /> Agregar artículo
        </Button>
        <p className="font-display text-lg font-bold" data-testid="total-venta">Total: {money(total)}</p>
      </div>
    </div>
  );
};

export const hayFaltantes = (lineas) => lineas.some(faltaExistencia);
